import React from 'react';
import { Box, Typography } from '@mui/material';
import useTenantBranding from './useTenantBranding';
import brand from '../shell/brand';
import QuadrantLoader from '../shell/QuadrantLoader';
import Wordmark from '../shell/Wordmark';
import mark from './product-mark.png';

// Rendered by LoginLayout on desktop and by LoginPage on mobile, so resolving the
// tenant here brands both without either needing to know about tenants.
const LogoImage = () => {
  const branding = useTenantBranding();
  // The customer's own name if this is their sign-in URL, otherwise the product's - which is
  // whichever product this address sells.
  const name = branding?.appName || brand.wordmark;
  const logo = branding?.logoDataUrl;
  // The mark beside the name is whichever product this address sells: the one that ships with the
  // software, or the one the brand brings with it.
  const productMark = brand.mark || mark;
  // Some products are drawn rather than typed. Where the brand supplies a picture of its own name,
  // that picture is the whole signature - shown instead of the mark AND the typed wordmark, because
  // it already says the name and putting both on the page prints it twice. A customer's own logo
  // still wins over it: that one says whose yard this is.
  const signature = !logo && brand.wordmarkImage;
  // Some products sign themselves with their own name rather than a mark beside it. Drawn in type
  // and animated once, so it costs nothing to fetch and is sharp at any size - and the colour is in
  // the letters, so it sits on whatever this page's background happens to be.
  const animated = !logo && !signature && brand.animatedWordmark;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: 'center', py: 2, px: 2, maxWidth: '100%',
    }}
    >
      {animated ? (
        <QuadrantLoader />
      ) : signature ? (
        <Box
          component="img"
          src={brand.wordmarkImage}
          alt={brand.name}
          sx={{ maxWidth: 320, maxHeight: 110, objectFit: 'contain' }}
        />
      ) : logo ? (
        <Box
          component="img"
          src={logo}
          alt={name}
          sx={{ maxWidth: 180, maxHeight: 64, objectFit: 'contain' }}
        />
      ) : (
        // The product's own mark, where the letter in a box used to be. Its own proportions, not a
        // box it has to fit: forcing a square letterboxed it and left it sitting low beside the
        // wordmark. The nudge upward answers the swoosh, which hangs below the ring and drags the
        // mark's geometric centre lower than the eye reads it.
        <Box
          component="img"
          src={productMark}
          alt=""
          sx={{
            height: 42, width: 'auto', flexShrink: 0, display: 'block', mt: '-3px',
          }}
        />
      )}
      {/* Not beside a picture that already says it. The signature IS the name - setting it in type
          as well printed the product twice, once drawn and once typed. */}
      {!signature && !animated && (
      <Typography sx={{
        // A customer's own name is mixed-case and can be long; the wide tracking
        // that suits the default wordmark would make it hard to read.
        fontSize: branding ? 20 : 24,
        fontWeight: 600,
        // Tracking follows the letterforms rather than whose name it is. Three points suits
        // capitals; on a mixed-case name it reads as separated letters, and it would pull apart the
        // two halves of a name set in two colours.
        letterSpacing: branding || name !== name.toUpperCase() ? 0.5 : 3,
        lineHeight: 1.2,
      }}
      >
        <Wordmark name={name} />
      </Typography>
      )}
    </Box>
  );
};

export default LogoImage;
